import { memo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, MultiSelectSearch } from '@/components/ui';
import { useSalesMarkets } from '@/hooks/useSalesMarkets';
import { useMediaTypes } from '@/hooks/useMediaTypes';
import type { ChartingQueueFilters } from '@/hooks/useCharting';

const STATUS_OPTIONS = [
{ value: 'open', label: 'Open' },
{ value: 'in_progress', label: 'In Progress' },
{ value: 'completed', label: 'Completed' },
{ value: 'approval_required_by_exception', label: 'Exception Required' },
{ value: 'revision_required_by_sales', label: 'Revision Required' },
];

interface QueueFiltersProps {
filters: ChartingQueueFilters;
onFilterChange: (key: keyof ChartingQueueFilters, value: ChartingQueueFilters[keyof ChartingQueueFilters]) => void;
onClearFilters: () => void;
}

export const QueueFilters = memo(function QueueFilters({
filters,
onFilterChange,
onClearFilters,
}: QueueFiltersProps) {
const { data: markets, isLoading: isLoadingMarkets } = useSalesMarkets();
const { data: mediaTypes, isLoading: isLoadingMediaTypes } = useMediaTypes();

const selectedMarkets = filters.markets || [];
const selectedMediaTypes = filters.mediaTypes || [];
const selectedStatuses = filters.chartingStatuses || [];

const handleMarketSelectionChange = (marketCodes: string[]) => {
onFilterChange('markets', marketCodes.length > 0 ? marketCodes : undefined);
};

const handleMediaTypeSelectionChange = (values: string[]) => {
onFilterChange('mediaTypes', values.length > 0 ? values : undefined);
};

const handleStatusSelectionChange = (values: string[]) => {
onFilterChange('chartingStatuses', values.length > 0 ? values : undefined);
};

// Use market_code as value (backend filters on ili.market_code) and display_name as label
const marketOptions = markets?.map(m => ({
value: m.market_code,
label: m.display_name || m.market_name
})) || [];

return (
<Card>
<CardHeader>
<CardTitle>Filters</CardTitle>
</CardHeader>
<CardContent>
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
<div>
<MultiSelectSearch
label="Line Item Status"
options={STATUS_OPTIONS}
selectedValues={selectedStatuses}
onSelectionChange={handleStatusSelectionChange}
placeholder="Search statuses..."
itemLabel="status"
/>
</div>

<div>
<label className="block text-sm font-medium text-neutral-600 mb-1">
Assignment
</label>
<select
value={
filters.unassignedOnly
? 'unassigned'
: filters.myAssignments
? 'mine'
: ''
}
onChange={(e) => {
if (e.target.value === 'unassigned') {
onFilterChange('unassignedOnly', true);
onFilterChange('myAssignments', undefined);
} else if (e.target.value === 'mine') {
onFilterChange('unassignedOnly', undefined);
onFilterChange('myAssignments', true);
} else {
onFilterChange('unassignedOnly', undefined);
onFilterChange('myAssignments', undefined);
}
}}
className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
>
<option value="">All Items</option>
<option value="mine">My Assignments</option>
<option value="unassigned">Unassigned</option>
</select>
</div>

<div>
<MultiSelectSearch
label="Market"
options={marketOptions}
selectedValues={selectedMarkets}
onSelectionChange={handleMarketSelectionChange}
placeholder="Search markets..."
isLoading={isLoadingMarkets}
/>
</div>

<div>
<MultiSelectSearch
label="Media Type"
options={mediaTypes || []}
selectedValues={selectedMediaTypes}
onSelectionChange={handleMediaTypeSelectionChange}
placeholder="Search media types..."
isLoading={isLoadingMediaTypes}
itemLabel="media type"
/>
</div>

<div>
<label className="block text-sm font-medium text-neutral-600 mb-1">
Sort By
</label>
<select
value={filters.sortBy || 'expiration'}
onChange={(e) => onFilterChange('sortBy', e.target.value)}
className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
>
<option value="expiration">Expiration Date</option>
<option value="created">Created Date</option>
<option value="market">Market</option>
<option value="detailed_status">Detailed Status</option>
</select>
</div>

<div>
<label className="block text-sm font-medium text-neutral-600 mb-1">
Order
</label>
<select
value={filters.sortOrder || 'asc'}
onChange={(e) => onFilterChange('sortOrder', e.target.value as 'asc' | 'desc')}
className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
>
<option value="asc">Ascending</option>
<option value="desc">Descending</option>
</select>
</div>
</div>

<div className="mt-4 flex gap-2">
<Button onClick={onClearFilters} variant="secondary">
Clear Filters
</Button>
</div>
</CardContent>
</Card>
);
});
